// src/features/metricas/components/MetricasEmpresas.jsx
import React, { useState, useEffect, useMemo } from "react";
import ResponsiveTable from "../../../components/UI/ResponsiveTable";
import {
  BuildingOfficeIcon,
  DocumentTextIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  CalendarDaysIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  UserGroupIcon,
  HashtagIcon,
} from "@heroicons/react/24/outline";

import { useAuth } from "../../../hooks/useAuth";
import { metricasService } from "../../../api/metricas";

const MetricasEmpresas = ({
  metricas,
  filters,
  onExport,
  onFiltersChange,
  loading = false,
}) => {
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { isManager } = useAuth();
  
  // Estados para filtros
  const [fechaInicioFiltro, setFechaInicioFiltro] = useState("");
  const [fechaFinFiltro, setFechaFinFiltro] = useState("");
  const [empresaFiltro, setEmpresaFiltro] = useState("todas");
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);

  // 🔥 Estados para exportación
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Formatear valor monetario
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "$0";
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Formatear horas
  const formatHours = (hours) => {
    if (!hours && hours !== 0) return "0 hrs";
    return `${parseFloat(hours).toFixed(2)} hrs`;
  };

  // Formatear fecha para input
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  // 🔥 FUNCIÓN ACTUALIZADA: Exportación por empresa usando onExport del padre
  const handleExportEmpresaExcel = async () => {
    if (
      empresaFiltro !== "todas" &&
      (!selectedEmpresa || !selectedEmpresa.nombre)
    ) {
      setExportError("Selecciona una empresa para exportar");
      setTimeout(() => setExportError(null), 3000);
      return;
    }

    if (!metricas || metricas.length === 0) {
      setExportError("No hay datos para exportar");
      setTimeout(() => setExportError(null), 3000);
      return;
    }

    setExporting(true);
    setExportError(null);
    setExportSuccess(false);

    try {
      //console.log("📤 Exportando empresa:", {
      //  empresa: selectedEmpresa?.nombre || "todas",
      //  empresaFiltro: empresaFiltro,
     // });

      // Preparar filtros para exportación
      const exportFilters = {
        ...filters,
        ...(fechaInicioFiltro && { fecha_inicio: fechaInicioFiltro }),
        ...(fechaFinFiltro && { fecha_fin: fechaFinFiltro }),
        ...(empresaFiltro !== "todas" &&
          selectedEmpresa && { empresa_nombre: selectedEmpresa.nombre }),
      };

      //console.log("📋 Filtros para exportación:", exportFilters);

      // 🔥 OPCIÓN 1: Si hay función onExport del padre, usarla
      if (typeof onExport === "function") {
        //console.log("📤 Usando onExport del padre");
        await onExport(exportFilters);
      }
      // 🔥 OPCIÓN 2: Exportación específica de empresa
      else if (empresaFiltro !== "todas" && selectedEmpresa) {
        //console.log("📤 Usando exportación específica de empresa");
        await metricasService.exportMetricasEmpresaExcel(
          selectedEmpresa.nombre,
          exportFilters,
        );
      }
      // 🔥 OPCIÓN 3: Exportación general de empresas
      else {
        //console.log("📤 Usando exportación general de empresas");
        await metricasService.exportMetricasEmpresasExcel(exportFilters);
      }

      setExportSuccess(true);
      //console.log("✅ Exportación empresa iniciada correctamente");

      // Ocultar mensaje de éxito después de 3 segundos
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      //console.error("❌ Error en exportación empresa:", error);

      let errorMessage = "Error al exportar el archivo";

      if (error.message.includes("No module named")) {
        errorMessage =
          "El servidor necesita instalar openpyxl. Usando exportación alternativa...";
        setTimeout(() => {
          tryExportReactFallback();
        }, 1000);
      } else if (error.message.includes("Network Error")) {
        errorMessage =
          "Error de conexión con el servidor. Verifica tu conexión a internet.";
      } else if (
        error.message.includes("401") ||
        error.message.includes("403")
      ) {
        errorMessage =
          "No tienes permisos para exportar métricas. Contacta al administrador.";
      } else if (error.message.includes("404")) {
        errorMessage =
          "El servicio de exportación no está disponible. Contacta al administrador.";
      } else {
        errorMessage = `Error: ${error.message}`;
      }

      setExportError(errorMessage);
      setTimeout(() => setExportError(null), 5000);
    } finally {
      setExporting(false);
    }
  };

  // 🔥 Exportación React como fallback
  const tryExportReactFallback = async () => {
    try {
      const { exportToExcel } = await import("../../../utils/exportExcel");
      const filename =
        empresaFiltro !== "todas" && selectedEmpresa
          ? `metricas-empresa-${selectedEmpresa.nombre}-${new Date().toISOString().split("T")[0]}`
          : `metricas-empresas-${new Date().toISOString().split("T")[0]}`;

      exportToExcel(metricas, filename, "Métricas Empresas");

      setExportError("✅ Exportado localmente (sin servidor)");
      setTimeout(() => setExportError(null), 3000);
    } catch (fallbackError) {
      //console.error("Fallback también falló:", fallbackError);
      setExportError("Error en exportación alternativa");
      setTimeout(() => setExportError(null), 3000);
    }
  };

  // Obtener fechas mínima y máxima de los datos
  const { fechaMinima, fechaMaxima } = useMemo(() => {
    if (!metricas || metricas.length === 0) {
      return { fechaMinima: "", fechaMaxima: "" };
    }

    let minDate = null;
    let maxDate = null;

    metricas.forEach((item) => {
      if (item.fecha_acta) {
        const fecha = new Date(item.fecha_acta);
        if (!minDate || fecha < minDate) minDate = fecha;
        if (!maxDate || fecha > maxDate) maxDate = fecha;
      }
    });

    return {
      fechaMinima: minDate ? formatDateForInput(minDate) : "",
      fechaMaxima: maxDate ? formatDateForInput(maxDate) : "",
    };
  }, [metricas]);

  // Obtener lista única de empresas
  const empresasUnicas = useMemo(() => {
    if (!metricas || metricas.length === 0) return [];

    const empresasMap = new Map();
    metricas.forEach((item) => {
      if (item.empresa_nombre) {
        const key = `${item.empresa_nit}_${item.empresa_nombre}`;
        if (!empresasMap.has(key)) {
          empresasMap.set(key, {
            id: item.empresa_id || item.empresa_nit,
            nombre: item.empresa_nombre,
            nit: item.empresa_nit,
          });
        }
      }
    });

    return Array.from(empresasMap.values()).sort((a, b) =>
      a.nombre.localeCompare(b.nombre),
    );
  }, [metricas]);

  // 🔥 Actualizar empresa seleccionada cuando cambia el filtro
  useEffect(() => {
    if (empresaFiltro !== "todas") {
      const empresa = empresasUnicas.find(
        (e) => e.nit === empresaFiltro || e.id?.toString() === empresaFiltro,
      );
      setSelectedEmpresa(empresa || null);
    } else {
      setSelectedEmpresa(null);
    }
  }, [empresaFiltro, empresasUnicas]);

  // Filtrar métricas por fecha y empresa seleccionados
  const metricasFiltradas = useMemo(() => {
    if (!metricas || metricas.length === 0) return [];

    let filtradas = [...metricas];

    // Filtrar por rango de fechas
    if (fechaInicioFiltro) {
      const inicio = new Date(fechaInicioFiltro);
      inicio.setHours(0, 0, 0, 0);
      filtradas = filtradas.filter((item) => {
        if (!item.fecha_acta) return false;
        const fechaActa = new Date(item.fecha_acta);
        fechaActa.setHours(0, 0, 0, 0);
        return fechaActa >= inicio;
      });
    }

    if (fechaFinFiltro) {
      const fin = new Date(fechaFinFiltro);
      fin.setHours(23, 59, 59, 999);
      filtradas = filtradas.filter((item) => {
        if (!item.fecha_acta) return false;
        const fechaActa = new Date(item.fecha_acta);
        return fechaActa <= fin;
      });
    }

    // Filtrar por empresa
    if (empresaFiltro !== "todas") {
      filtradas = filtradas.filter(
        (item) =>
          item.empresa_nit && item.empresa_nit.toString() === empresaFiltro,
      );
    }

    return filtradas;
  }, [metricas, fechaInicioFiltro, fechaFinFiltro, empresaFiltro]);

  // 🔥 CALCULAR TOTALES DE LA TABLA
  const totalesTabla = useMemo(() => {
    if (!metricasFiltradas || metricasFiltradas.length === 0) {
      return {
        cantidadRegistros: 0,
        totalHoras: 0,
        totalValor: 0,
        empresasUnicas: 0,
        profesionalesUnicos: 0,
        actasUnicas: 0,
      };
    }

    const totalHoras = metricasFiltradas.reduce((sum, item) => {
      return sum + (parseFloat(item.duracion_horas) || 0);
    }, 0);

    const totalValor = metricasFiltradas.reduce((sum, item) => {
      return sum + (parseFloat(item.valor_total_acta) || 0);
    }, 0);

    const empresasUnicas = new Set(
      metricasFiltradas.map((item) => item.empresa_nit || item.empresa_nombre),
    ).size;

    const profesionalesUnicos = new Set(
      metricasFiltradas.map(
        (item) => item.profesional_id || item.profesional_username,
      ),
    ).size;

    const actasUnicas = new Set(
      metricasFiltradas.map((item) => item.numero_acta),
    ).size;

    return {
      cantidadRegistros: metricasFiltradas.length,
      totalHoras,
      totalValor,
      empresasUnicas,
      profesionalesUnicos,
      actasUnicas,
    };
  }, [metricasFiltradas]);

  // Calcular datos paginados
  const paginatedData = useMemo(() => {
    if (!metricasFiltradas || metricasFiltradas.length === 0) return [];

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return metricasFiltradas.slice(startIndex, endIndex);
  }, [metricasFiltradas, currentPage, itemsPerPage]);

  // Calcular número total de páginas
  const totalPages = useMemo(() => {
    if (!metricasFiltradas || metricasFiltradas.length === 0) return 0;
    return Math.ceil(metricasFiltradas.length / itemsPerPage);
  }, [metricasFiltradas, itemsPerPage]);

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, fechaInicioFiltro, fechaFinFiltro, empresaFiltro]);

  // Manejar cambio de página
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Manejar cambio de items por página
  const handleItemsPerPageChange = (e) => {
    const value = parseInt(e.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Manejar cambio de fecha inicio
  const handleFechaInicioChange = (e) => {
    const newValue = e.target.value;
    setFechaInicioFiltro(newValue);

    // 🔥 Llama a onFiltersChange para actualizar los filtros en el padre
    if (onFiltersChange) {
      onFiltersChange({
        fecha_inicio: newValue,
        // Mantener otros filtros
        ...filters,
      });
    }
  };

  // Manejar cambio de fecha fin
  const handleFechaFinChange = (e) => {
    const newValue = e.target.value;
    setFechaFinFiltro(newValue);

    // 🔥 Llama a onFiltersChange para actualizar los filtros en el padre
    if (onFiltersChange) {
      onFiltersChange({
        fecha_fin: newValue,
        ...filters,
      });
    }
  };

  // Manejar cambio de empresa
  const handleEmpresaChange = (e) => {
    const newValue = e.target.value;
    setEmpresaFiltro(newValue);

    // 🔥 Llama a onFiltersChange para actualizar los filtros en el padre
    if (onFiltersChange) {
      onFiltersChange({
        empresa_filtro: newValue,
        ...filters,
      });
    }
  };

  // Limpiar filtros de fecha
  const limpiarFiltrosFecha = () => {
    setFechaInicioFiltro("");
    setFechaFinFiltro("");
  };

  // 🔥 Botones de exportación - VERSIÓN MEJORADA
  const ExportButtons = () => {
    const hasData = metricas && metricas.length > 0;
    const hasEmpresaSelected = empresaFiltro !== "todas";

    const buttonText =
      hasEmpresaSelected && selectedEmpresa
        ? `Exportar ${selectedEmpresa.nombre}`
        : "Selecciona Una Empresa";

    return (
      <div className="mb-4">
        {/* Panel de exportación */}
        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg mb-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div className="flex-1 mb-3 md:mb-0 md:mr-4">
              <div className="flex items-center mb-2">
                <ArrowDownTrayIcon className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="text-sm font-semibold text-green-900">
                  Exportar Reporte
                </h3>
              </div>
              <p className="text-xs text-green-700">
                {hasData
                  ? `${metricas.length} registros disponibles`
                  : "No hay datos disponibles"}
                {hasEmpresaSelected && selectedEmpresa && (
                  <span className="block mt-1 font-medium text-green-800">
                    Empresa seleccionada: {selectedEmpresa.nombre}
                  </span>
                )}
                {!hasEmpresaSelected && hasData && (
                  <span className="block mt-1 text-green-700">
                    Se exportarán todas las empresas
                  </span>
                )}
              </p>

              {/* Mostrar si onExport está disponible */}
              {typeof onExport === "function" && (
                <p className="text-xs text-green-700 mt-1">
                  ✓ Usando función de exportación del componente padre
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              {/* Botón Exportar Empresa */}
              <button
                onClick={handleExportEmpresaExcel}
                disabled={exporting || !hasData || !selectedEmpresa }
                className={`
                                    inline-flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-all duration-200
                                    ${
                                      exporting
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : !hasData
                                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                          : "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700"
                                    }
                                `}
                title={
                  !hasData
                    ? "No hay datos para exportar"
                    : hasEmpresaSelected
                      ? `Exportar ${selectedEmpresa?.nombre}`
                      : "Exportar todas las empresas"
                }
              >
                {exporting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Exportando...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    {buttonText}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Mensajes de estado */}
          {exportError && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 animate-pulse">
              <div className="flex items-center">
                <XCircleIcon className="h-4 w-4 mr-2" />
                {exportError}
              </div>
            </div>
          )}

          {exportSuccess && (
            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700 animate-pulse">
              <div className="flex items-center">
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                ¡Archivo Excel descargándose automáticamente!
              </div>
              {hasEmpresaSelected && selectedEmpresa && (
                <p className="mt-1 text-green-600">
                  Contiene métricas de:{" "}
                  <strong>{selectedEmpresa.nombre}</strong>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Información sobre filtros activos */}
        {(fechaInicioFiltro ||
          fechaFinFiltro ||
          empresaFiltro !== "todas" ||
          Object.values(filters).some((v) => v)) && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex items-center mb-2">
              <FunnelIcon className="h-4 w-4 text-blue-500 mr-2" />
              <span className="text-sm font-medium text-blue-800">
                Filtros activos:
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {fechaInicioFiltro && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Desde:{" "}
                  {new Date(fechaInicioFiltro).toLocaleDateString("es-CO")}
                </span>
              )}
              {fechaFinFiltro && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  Hasta: {new Date(fechaFinFiltro).toLocaleDateString("es-CO")}
                </span>
              )}
              {empresaFiltro !== "todas" && selectedEmpresa && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Empresa: {selectedEmpresa.nombre}
                </span>
              )}
              {Object.entries(filters)
                .filter(([_, value]) => value)
                .map(([key, value]) => (
                  <span
                    key={key}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {key.replace("_", " ")}: {value}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      header: "Empresa",
      accessor: "empresa_nombre",
      cell: (row) => (
        <div className="flex items-center">
          <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">
              {row.empresa_nombre}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {row.empresa_nit || "Sin NIT"}
            </p>
          </div>
        </div>
      ),
    },
    {
      header: "Profesional",
      accessor: "profesional_nombre_completo",
      cell: (row) => (
        <div className="flex items-center">
          <UserIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">
              {row.profesional_nombre_completo}
            </p>
            <p className="text-xs text-gray-500 truncate">
              @{row.profesional_username}
            </p>
          </div>
        </div>
      ),
    },
    {
      header: "Actas",
      accessor: "numero_acta",
      align: "right",
      cell: (row) => (
        <div className="text-right">
          <div className="flex items-center justify-end">
            <DocumentTextIcon className="h-4 w-4 text-blue-500 mr-1 flex-shrink-0" />
            <span className="font-medium">{row.numero_acta}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Fecha",
      accessor: "fecha_acta",
      align: "center",
      cell: (row) => {
        const fecha = row.fecha_acta
          ? new Date(row.fecha_acta).toLocaleDateString("es-CO")
          : "Sin fecha";
        return <div className="text-center text-sm text-gray-600">{fecha}</div>;
      },
    },
    {
      header: "Horas",
      accessor: "duracion_horas",
      align: "right",
      cell: (row) => (
        <div className="text-right">
          <div className="flex items-center justify-end">
            <ClockIcon className="h-4 w-4 text-green-500 mr-1 flex-shrink-0" />
            <span className="font-medium">
              {formatHours(row.duracion_horas)}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Valor Hora",
      accessor: "profesional_valor_hora",
      align: "right",
      cell: (row) => (
        <div className="text-right">
          <div className="flex items-center justify-end">
            <CurrencyDollarIcon className="h-4 w-4 text-purple-500 mr-1 flex-shrink-0" />
            <span className="font-medium">
              {formatCurrency(row.profesional_valor_hora)}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Valor Total",
      accessor: "valor_total_acta",
      align: "right",
      cell: (row) => {
        return (
          <div className="text-right">
            {formatCurrency(row.valor_total_acta)}
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-4 md:p-6">
      {/* 🔥 Botones de exportación */}
      <ExportButtons />

      {/* Selectores de filtros */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Selector de rango de fechas */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <CalendarDaysIcon className="h-5 w-5 text-gray-500 mr-2" />
                <h3 className="text-sm font-medium text-gray-700">
                  Filtrar por Fecha
                </h3>
              </div>
              {(fechaInicioFiltro || fechaFinFiltro) && (
                <button
                  onClick={limpiarFiltrosFecha}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Limpiar fechas
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Fecha de inicio
                </label>
                <input
                  type="date"
                  value={fechaInicioFiltro}
                  onChange={handleFechaInicioChange}
                  max={fechaFinFiltro || fechaMaxima}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  title="Selecciona la fecha inicial del rango"
                />
                {fechaMinima && (
                  <p className="text-xs text-gray-500 mt-1">
                    Fecha mínima disponible:{" "}
                    {new Date(fechaMinima).toLocaleDateString("es-CO")}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Fecha de fin
                </label>
                <input
                  type="date"
                  value={fechaFinFiltro}
                  onChange={handleFechaFinChange}
                  min={fechaInicioFiltro || fechaMinima}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  title="Selecciona la fecha final del rango"
                />
                {fechaMaxima && (
                  <p className="text-xs text-gray-500 mt-1">
                    Fecha máxima disponible:{" "}
                    {new Date(fechaMaxima).toLocaleDateString("es-CO")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Selector de empresa */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex flex-col h-full">
            <div className="flex items-center mb-3">
              <BuildingOfficeIcon className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-sm font-medium text-gray-700">
                Filtrar por Empresa
              </h3>
            </div>
            <select
              value={empresaFiltro}
              onChange={handleEmpresaChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm mb-4"
            >
              <option value="todas">Todas las empresas</option>
              {empresasUnicas.map((empresa) => (
                <option
                  key={empresa.nit || empresa.id}
                  value={empresa.nit || empresa.id}
                >
                  {empresa.nombre} {empresa.nit ? `- ${empresa.nit}` : ""}
                </option>
              ))}
            </select>
            <div className="text-xs text-gray-600 mt-2">
              {empresaFiltro === "todas" ? (
                <span className="text-blue-600">
                  ✓ Mostrando todas las empresas
                </span>
              ) : selectedEmpresa ? (
                <span className="text-green-600">
                  ✓ Filtrado por: <strong>{selectedEmpresa.nombre}</strong>
                </span>
              ) : (
                <span className="text-gray-500">Selecciona una empresa</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 🔥 TOTALIZADORES DE LA TABLA - EN LA PARTE SUPERIOR (igual que MetricasContadores) */}
      <div className="mb-6 bg-gradient-to-r from-gray-50 to-green-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center mb-3">
          <ChartBarIcon className="h-5 w-5 text-green-600 mr-2" />
          <h3 className="text-sm font-medium text-gray-900">
            Totales de la Tabla (Empresas)
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Total Registros */}
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-blue-100">
                <DocumentTextIcon className="h-4 w-4 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Actas</p>
                <p className="text-lg font-bold text-blue-700">
                  {totalesTabla.cantidadRegistros.toLocaleString("es-CO")}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Cantidad de filas en la tabla
            </p>
          </div>

          {/* Total Horas */}
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-green-100">
                <ClockIcon className="h-4 w-4 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Total Horas</p>
                <p className="text-lg font-bold text-green-700">
                  {formatHours(totalesTabla.totalHoras)}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Suma de horas trabajadas
            </p>
          </div>

          {/* Total Valor */}
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-purple-100">
                <CurrencyDollarIcon className="h-4 w-4 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Total Valor</p>
                <p className="text-lg font-bold text-purple-700">
                  {formatCurrency(totalesTabla.totalValor)}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Valor total generado</p>
          </div>

          {/* Empresas Únicas */}
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-indigo-100">
                <BuildingOfficeIcon className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Empresas</p>
                <p className="text-lg font-bold text-indigo-700">
                  {totalesTabla.empresasUnicas}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Empresas únicas</p>
          </div>

          {/* Profesionales Únicos */}
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-orange-100">
                <UserGroupIcon className="h-4 w-4 text-orange-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">
                  Profesionales
                </p>
                <p className="text-lg font-bold text-orange-700">
                  {totalesTabla.profesionalesUnicos}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Contadores únicos</p>
          </div>
        </div>

        {/* Información adicional */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center text-xs text-gray-600">
            <svg
              className="h-3 w-3 mr-1 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>
              Totales calculados sobre los {metricasFiltradas.length} registros
              filtrados
            </span>
          </div>
        </div>
      </div>

      {/* Tabla de métricas */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Detalle por Empresa
            </h2>
            <p className="text-sm text-gray-500">
              Mostrando {paginatedData.length} de {metricasFiltradas.length}{" "}
              {metricasFiltradas.length === 1 ? "registro" : "registros"}
              {metricasFiltradas.length > itemsPerPage && (
                <span>
                  {" "}
                  · Página {currentPage} de {totalPages}
                </span>
              )}
              {(fechaInicioFiltro ||
                fechaFinFiltro ||
                empresaFiltro !== "todas") && <span> · Filtrado aplicado</span>}
            </p>
          </div>
          <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
            <div className="text-sm text-gray-500">
              <span className="hidden sm:inline">Ordenado por: </span>
              <span className="font-medium text-gray-900">
                Número de Acta (Descendente)
              </span>
            </div>

            {/* Selector de items por página */}
            {metricasFiltradas.length > 5 && (
              <div className="flex items-center space-x-2">
                <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
                  Mostrar:
                </label>
                <select
                  id="itemsPerPage"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* 🔥 CORRECCIÓN: Manejo de loading y empty states */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : !metricas || metricas.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay métricas disponibles
            </h3>
            <p className="text-gray-600">
              No se encontraron datos de empresas para mostrar.
            </p>
          </div>
        ) : metricasFiltradas.length === 0 ? (
          <div className="text-center py-12 bg-yellow-50 rounded-lg">
            <FunnelIcon className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay resultados con los filtros aplicados
            </h3>
            <p className="text-gray-600">
              Intenta ajustar los filtros de fecha o empresa.
            </p>
          </div>
        ) : (
          <>
            <ResponsiveTable
              columns={columns}
              data={paginatedData}
              emptyMessage="No hay datos para mostrar"
              striped={true}
              hover={true}
              className="border border-gray-200 rounded-lg overflow-hidden"
            />

            {/* Controles de paginación */}
            {totalPages > 1 && (
              <div className="flex flex-col md:flex-row items-center justify-between mt-4 px-2 gap-4">
                <div className="text-sm text-gray-700">
                  <span>
                    Mostrando {(currentPage - 1) * itemsPerPage + 1} a{" "}
                    {Math.min(
                      currentPage * itemsPerPage,
                      metricasFiltradas.length,
                    )}{" "}
                    de {metricasFiltradas.length} registros
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-md ${
                      currentPage === 1
                        ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Anterior
                  </button>

                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      if (pageNum < 1 || pageNum > totalPages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 flex items-center justify-center text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <>
                        <span className="px-1 text-gray-500">...</span>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          className={`w-8 h-8 flex items-center justify-center text-sm font-medium rounded-md ${
                            currentPage === totalPages
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-md ${
                      currentPage === totalPages
                        ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    Siguiente
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MetricasEmpresas;
